/**
 * useToolsBrowser Hook
 *
 * Hook for managing tool browser state (Custom/Builtin tools)
 * Migrated from tool-browser.js createCustom() function
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { useAvailableTools } from "./useTools";
import { useToolEnhancementConfig } from "@/features/system-settings/hooks/useSystemConfig";
import { useI18n } from "@/i18n";
import type {
  ToolMode,
  CustomToolGroup,
  CustomToolsConfig,
  BuiltinToolsConfig,
  BuiltinToolkit,
} from "../../../types/tools";

interface UseToolsBrowserOptions {
  mode: ToolMode;
  initialConfig?: CustomToolsConfig | string;
  onApply?: (config: string) => void;
}

export function useToolsBrowser({
  mode,
  initialConfig,
  onApply,
}: UseToolsBrowserOptions) {
  const { messages } = useI18n();
  const t = messages.agentConfig.toolsBrowser;

  // State - no longer need activeTab since we use mode directly
  const [enabledTools, setEnabledTools] = useState<string[]>([]);
  const [enabledSubTools, setEnabledSubTools] = useState<string[]>([]); // For builtin toolkit sub-tools
  const [filterCategory, setFilterCategory] = useState<string | null>(null);

  // Fetch available tools
  const {
    data: toolsData,
    isLoading: toolsLoading,
    error,
    refetch,
  } = useAvailableTools();

  // Fetch enhancement config for display names
  const { data: enhancementConfig, isLoading: enhancementLoading } =
    useToolEnhancementConfig();

  const isLoading = toolsLoading || enhancementLoading;

  // Helper to get display name for a tool
  // Priority: backend display_name > local enhancement config > tool name
  const getDisplayName = useCallback(
    (toolName: string): string => {
      // First, check if it's a builtin toolkit (already has display_name from backend)
      const builtinToolkit = toolsData?.builtin_toolkits?.find(
        (t) => t.name === toolName
      );
      if (builtinToolkit?.display_name) {
        return builtinToolkit.display_name;
      }

      // Second, check if it's a custom tool (already has display_name from backend)
      const customTool = toolsData?.custom_tools?.find(
        (t) => t.name === toolName
      );
      if (customTool?.display_name) {
        return customTool.display_name;
      }

      // Fallback to local enhancement config
      const enhancement = enhancementConfig?.tools?.[toolName];
      if (enhancement) {
        // Prefer Chinese, fallback to English
        const displayName =
          enhancement.display_name_cn || enhancement.display_name_en;
        if (displayName) return displayName;
      }

      return toolName;
    },
    [toolsData, enhancementConfig]
  );

  // Parse initial configuration
  useEffect(() => {
    if (!initialConfig) {
      setEnabledTools([]);
      setEnabledSubTools([]);
      return;
    }

    try {
      // Handle different input types
      let parsed = initialConfig;

      // If it's a string, try to parse it as JSON
      if (typeof initialConfig === "string") {
        // Empty string means no tools
        if (!initialConfig.trim()) {
          setEnabledTools([]);
          setEnabledSubTools([]);
          return;
        }
        parsed = JSON.parse(initialConfig);
      }

      // New format: Array of tool names (legacy)
      if (Array.isArray(parsed)) {
        console.log("Loading tools from array:", parsed);
        setEnabledTools(parsed);
        setEnabledSubTools([]);
      }
      // New builtin tools format with toolkits and enabled_tools
      else if (parsed && typeof parsed === "object" && "toolkits" in parsed) {
        const builtinConfig = parsed as BuiltinToolsConfig;
        console.log("Loading builtin tools config:", builtinConfig);
        setEnabledTools(builtinConfig.toolkits || []);
        setEnabledSubTools(builtinConfig.enabled_tools || []);
      }
      // Legacy format support (object with modules/enabled_tools)
      else if (parsed && typeof parsed === "object") {
        console.log("Loading tools from object:", parsed);
        setEnabledTools((parsed as CustomToolsConfig).enabled_tools || []);
        setEnabledSubTools([]);
      }
    } catch (e) {
      console.warn("Could not parse initial config:", e, initialConfig);
      setEnabledTools([]);
      setEnabledSubTools([]);
    }
  }, [initialConfig]);

  // Computed values
  const builtinToolkits = toolsData?.builtin_toolkits || [];
  const customTools = toolsData?.custom_tools || [];
  const categories = toolsData?.categories || { builtin: [], custom: [] };
  const totalTools = toolsData?.total_tools || 0;

  const selectedToolCount = enabledTools.length;

  const tExtended = t as Record<string, string> | undefined;
  const browserTitle =
    mode === "builtin"
      ? tExtended?.builtinToolsBrowser || "Builtin Tools Browser"
      : tExtended?.customToolsBrowser || "Custom Tools Browser";

  // Detect invalid tools (tools that are selected but don't exist in available tools)
  const invalidTools = useMemo(() => {
    const invalid = new Set<string>();

    if (mode === "builtin") {
      // For builtin mode, check if toolkit exists
      const availableToolkitNames = new Set(builtinToolkits.map((t) => t.name));
      enabledTools.forEach((toolName) => {
        if (!availableToolkitNames.has(toolName)) {
          invalid.add(toolName);
        }
      });

      // Also check sub-tools
      const availableSubToolNames = new Set<string>();
      builtinToolkits.forEach((toolkit) => {
        toolkit.tools?.forEach((tool) => {
          availableSubToolNames.add(tool.full_name);
        });
      });
      enabledSubTools.forEach((toolName) => {
        if (!availableSubToolNames.has(toolName)) {
          invalid.add(toolName);
        }
      });
    } else {
      // For custom mode, check if tool exists
      const availableToolNames = new Set(customTools.map((t) => t.name));
      enabledTools.forEach((toolName) => {
        if (!availableToolNames.has(toolName)) {
          invalid.add(toolName);
        }
      });
    }

    return invalid;
  }, [mode, enabledTools, enabledSubTools, builtinToolkits, customTools]);

  const hasInvalidTools = invalidTools.size > 0;

  // Generate configuration JSON
  const generatedConfig = useMemo(() => {
    if (enabledTools.length === 0) {
      return "";
    }

    // For builtin mode, use new format with toolkits and enabled_tools
    if (mode === "builtin") {
      const config: BuiltinToolsConfig = {
        toolkits: enabledTools,
      };
      // Only include enabled_tools if some sub-tools are selected
      if (enabledSubTools.length > 0) {
        config.enabled_tools = enabledSubTools;
      }
      return JSON.stringify(config, null, 2);
    }

    // For custom mode, use simple array format
    return JSON.stringify(enabledTools, null, 2);
  }, [enabledTools, enabledSubTools, mode]);

  // Get filtered builtin toolkits
  const filteredBuiltinToolkits = useMemo(() => {
    if (!filterCategory) {
      return builtinToolkits;
    }
    return builtinToolkits.filter((t) => t.category === filterCategory);
  }, [builtinToolkits, filterCategory]);

  // Get custom tools grouped by category
  const groupedCustomTools = useMemo(() => {
    const filtered = filterCategory
      ? customTools.filter((t) => t.category === filterCategory)
      : customTools;

    // Group by category
    const grouped: Record<string, CustomToolGroup> = {};
    filtered.forEach((tool) => {
      const cat = tool.category || "uncategorized";
      if (!grouped[cat]) {
        grouped[cat] = {
          category: cat,
          module: tool.module,
          tools: [],
          tool_count: 0,
        };
      }
      grouped[cat].tools.push(tool);
      grouped[cat].tool_count++;
    });

    return Object.values(grouped);
  }, [customTools, filterCategory]);

  // Actions
  const toggleTool = useCallback((toolName: string) => {
    setEnabledTools((prev) => {
      const index = prev.indexOf(toolName);
      if (index > -1) {
        return prev.filter((t) => t !== toolName);
      } else {
        return [...prev, toolName];
      }
    });
  }, []);

  // Toggle entire toolkit (select/deselect all sub-tools)
  const toggleModule = useCallback((moduleName: string) => {
    // Find the toolkit to get its sub-tools
    const toolkit = builtinToolkits.find((t: BuiltinToolkit) => t.name === moduleName);

    setEnabledTools((prev) => {
      const index = prev.indexOf(moduleName);
      if (index > -1) {
        // Deselect toolkit
        return prev.filter((t) => t !== moduleName);
      } else {
        // Select toolkit
        return [...prev, moduleName];
      }
    });

    // Also update sub-tools when toggling toolkit
    if (toolkit?.tools) {
      setEnabledSubTools((prev) => {
        const isSelected = enabledTools.includes(moduleName);
        if (isSelected) {
          // Deselecting toolkit - remove all its sub-tools
          return prev.filter(
            (toolName) => !toolkit.tools!.some((t) => t.full_name === toolName)
          );
        } else {
          // Selecting toolkit - add all its sub-tools
          const newTools = toolkit.tools!
            .map((t) => t.full_name)
            .filter((name) => !prev.includes(name));
          return [...prev, ...newTools];
        }
      });
    }
  }, [builtinToolkits, enabledTools]);

  // Toggle individual sub-tool within a toolkit
  const toggleSubTool = useCallback((fullName: string, toolkitName: string) => {
    const toolkit = builtinToolkits.find((t: BuiltinToolkit) => t.name === toolkitName);

    setEnabledSubTools((prev) => {
      const index = prev.indexOf(fullName);
      if (index > -1) {
        // Removing tool
        const next = prev.filter((t) => t !== fullName);

        // Check if any sub-tools from this toolkit are still selected
        const hasSelectedTools = toolkit?.tools?.some((t) => next.includes(t.full_name));
        if (!hasSelectedTools) {
          // Auto-deselect toolkit when no sub-tools selected
          setEnabledTools((prevTools) => prevTools.filter((t) => t !== toolkitName));
        }

        return next;
      } else {
        // Adding tool - auto-select toolkit if not already selected
        if (!enabledTools.includes(toolkitName)) {
          setEnabledTools((prevTools) => [...prevTools, toolkitName]);
        }
        return [...prev, fullName];
      }
    });
  }, [builtinToolkits, enabledTools]);

  const toggleCategory = useCallback(
    (category: string) => {
      const categoryTools = customTools.filter((t) => t.category === category);
      const allSelected = categoryTools.every((t) =>
        enabledTools.includes(t.name)
      );

      if (allSelected) {
        // Deselect all
        setEnabledTools((prev) =>
          prev.filter((name) => !categoryTools.some((t) => t.name === name))
        );
      } else {
        // Select all
        setEnabledTools((prev) => {
          const newTools = categoryTools
            .filter((t) => !prev.includes(t.name))
            .map((t) => t.name);
          return [...prev, ...newTools];
        });
      }
    },
    [customTools, enabledTools]
  );

  const isToolEnabled = useCallback(
    (toolName: string) => enabledTools.includes(toolName),
    [enabledTools]
  );

  const isModuleSelected = useCallback(
    (moduleName: string) => enabledTools.includes(moduleName),
    [enabledTools]
  );

  const isSubToolEnabled = useCallback(
    (fullName: string) => enabledSubTools.includes(fullName),
    [enabledSubTools]
  );

  const isCategorySelected = useCallback(
    (category: string) => {
      return customTools
        .filter((t) => t.category === category)
        .some((t) => enabledTools.includes(t.name));
    },
    [customTools, enabledTools]
  );

  const handleReopen = useCallback(
    (config?: CustomToolsConfig | BuiltinToolsConfig | string) => {
      console.log("Tool browser reopened, mode:", mode);

      // Clear previous selections
      setEnabledTools([]);
      setEnabledSubTools([]);

      // Parse the current config to restore selections
      if (config) {
        try {
          const parsed =
            typeof config === "string" ? JSON.parse(config) : config;

          if (Array.isArray(parsed)) {
            setEnabledTools(parsed);
          } else if (parsed && typeof parsed === "object" && "toolkits" in parsed) {
            // New builtin tools format
            const builtinConfig = parsed as BuiltinToolsConfig;
            setEnabledTools(builtinConfig.toolkits || []);
            setEnabledSubTools(builtinConfig.enabled_tools || []);
          } else if (parsed && typeof parsed === "object") {
            setEnabledTools((parsed as CustomToolsConfig).enabled_tools || []);
          }
        } catch (e) {
          console.warn("Could not parse config on reopen:", e);
        }
      }

      // Reload tools from API
      refetch();
    },
    [mode, refetch]
  );

  const handleApply = useCallback(() => {
    if (onApply) {
      onApply(generatedConfig);
    }
  }, [generatedConfig, onApply]);

  return {
    // State
    enabledTools,
    enabledSubTools,
    filterCategory,
    setFilterCategory,

    // Data
    builtinToolkits,
    customTools,
    categories,
    totalTools,
    filteredBuiltinToolkits,
    groupedCustomTools,

    // Computed
    selectedToolCount,
    browserTitle,
    generatedConfig,

    // Validation
    invalidTools,
    hasInvalidTools,

    // Loading/Error
    isLoading,
    error: error ? String(error) : null,

    // Actions
    toggleTool,
    toggleModule,
    toggleSubTool,
    toggleCategory,
    isToolEnabled,
    isModuleSelected,
    isSubToolEnabled,
    isCategorySelected,
    handleReopen,
    handleApply,
    refetch,

    // Enhancement helpers
    getDisplayName,
  };
}
